import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag } from "antd";
import {
  DeleteOutlined,
  EllipsisOutlined,
  LinkOutlined,
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useServiceOfferings from "hooks/olreadia/useServiceOfferings";
import ServiceOfferingDrawer from "./ServiceOfferingDrawer";
import ServiceOfferingDetailsDrawer from "./ServiceOfferingDetailsDrawer";
import { formatEnumLabel } from "../constants";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import { useAuth } from "contexts/AuthContext";

export default function ServiceOfferingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const { offerings, loading, error, page, list, create, update, remove } =
    useServiceOfferings();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [query, setQuery] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchPage = async (pag = pagination) => {
    await list({ page: pag.current - 1, size: pag.pageSize });
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setDrawerOpen(true);
  };

  const openDetails = (record) => {
    setSelected(record);
    setDetailsOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Remove service offering?",
      content: "This will delete the service offering.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        await remove(record.id, actorId);
        await fetchPage();
      },
    });
  };

  const handleSubmit = async (payload) => {
    if (editing?.id) {
      await update(editing.id, payload, actorId);
    } else {
      await create(payload, actorId);
    }
    setDrawerOpen(false);
    await fetchPage();
  };

  const filteredOfferings = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return offerings;
    return (offerings || []).filter((o) => {
      const code = o?.code ? String(o.code) : "";
      const name = o?.name ? String(o.name) : "";
      const category = o?.category ? String(o.category) : "";
      const status = o?.active ? "active" : "inactive";
      return (
        code.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        category.toLowerCase().includes(q) ||
        status.includes(q)
      );
    });
  }, [offerings, query]);

  const columns = useMemo(
    () => [
      { title: "Code", dataIndex: "code", key: "code", width: 220, ellipsis: true },
      { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 220,
        render: (val) => (val ? <Tag>{formatEnumLabel(val)}</Tag> : "-"),
      },
      {
        title: "Status",
        dataIndex: "active",
        key: "status",
        width: 120,
        render: (val) => (
          <Tag color={val ? "green" : "red"}>{val ? "ACTIVE" : "INACTIVE"}</Tag>
        )
      },
      {
        title: "Actions",
        key: "actions",
        width: 70,
        render: (_, record) => (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "attach",
                  label: "Attach",
                  icon: <LinkOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    navigate(paths.attachServiceOffering(record.code));
                  },
                },
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleEdit(record);
                  },
                },
                {
                  key: "remove",
                  label: "Remove",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleDelete(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" icon={<EllipsisOutlined />} onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="box"
          title="Service Offerings"
          subtitle="Manage service offerings and categories used by business entities and lines."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchPage()}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Service
          </Button>
        </Space>
      </div>

      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
        <Input.Search
          allowClear
          placeholder="Search by code, name, category, status"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 420 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => fetchPage()}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredOfferings}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: 900 }}
        size="small"
        pagination={{
          ...pagination,
          total: page.totalElements,
          showSizeChanger: true,
        }}
        onChange={(pag) => {
          const next = { current: pag.current, pageSize: pag.pageSize };
          setPagination(next);
          list({ page: next.current - 1, size: next.pageSize });
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".ant-dropdown")) return;
            if (e?.target?.closest?.(".ant-dropdown-trigger")) return;
            openDetails(record);
          },
        })}
      />

      <ServiceOfferingDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={loading}
        initialValues={editing}
      />

      <ServiceOfferingDetailsDrawer
        open={detailsOpen}
        offering={selected}
        onClose={() => setDetailsOpen(false)}
        onAttach={(record) => navigate(paths.attachServiceOffering(record.code))}
        onEdit={(record) => {
          setDetailsOpen(false);
          handleEdit(record);
        }}
      />
    </div>
  );
}
